import type { ID } from '@phi/newtype';
import type { BlobElement } from '@phi/onshape';
import type { Option } from '@vaaas/fpts/option';
import { SelectDocumentElement } from '@components/OnshapeExplorer';
import { type PropType, defineComponent, h, watch } from 'vue';
import { microtask } from '@vaaas/fpts/timing';

export type OnshapeSketchValue = {
    document: Option<ID<'document'>>;
    workspace: Option<ID<'workspace'>>;
    element: Option<BlobElement>;
}

export const SelectReferenceSketchFromOnshape = defineComponent({
    props: {
        value: {
            required: true,
            type: Object as PropType<OnshapeSketchValue>,
        },
    },

    emits: {
        'update:value': (x: OnshapeSketchValue) => true,
    },

    setup(props, { emit }) {
        const had_selected_document = props.value.document !== undefined;

        const copy = props.value;

        watch(
            () => props.value,
            (value) => {
                copy.document = value.document;
                copy.element = value.element;
                copy.workspace = value.workspace;
            },
        );

        const debounced_emit = microtask(() => emit('update:value', { ...copy }));

        return () => h(SelectDocumentElement, {
            document: props.value.document,
            workspace: props.value.workspace,
            can_select_document: !had_selected_document,
            elementFilter: x => x.elementType === 'BLOB' && x.dataType.startsWith('image/'),
            sub_heading: false,
            element: props.value.element,
            'onUpdate:element': x => emit('update:value', {
                ...props.value,
                element: x as Option<BlobElement>,
            }),
            'onUpdate:document': x => {
                if (!had_selected_document) {
                    copy.document = x;
                    debounced_emit();
                }
            },
            'onUpdate:workspace': x => {
                if (!had_selected_document) {
                    copy.workspace = x;
                    debounced_emit();
                }
            },
            placeholder: 'Search for an image file to load',
        });
    },
});
